import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Check, X } from '../components/icons';
import { PUBLIC_PLANS, ADVERTISING_PLAN, annualTotal, type PlanKey } from '../lib/pricing';

const plans = [
  {
    name: 'Free',
    price: 0,
    period: 'forever',
    tagline: 'Your business name on the directory.',
    cta: 'List your business',
    planKey: 'free' as const,
    highlighted: false,
    features: [
      { label: 'Business name listed', included: true },
      { label: 'Website link', included: true },
      { label: 'SEO backlink from MarineLink', included: true },
      { label: 'Standard search placement', included: true },
      { label: 'No expiry - free forever', included: true },
      { label: 'Phone number', included: false },
      { label: 'Email address', included: false },
      { label: 'Business description', included: false },
      { label: 'Photos & gallery', included: false },
      { label: 'Services list', included: false },
      { label: 'Category placement', included: false },
      { label: 'Featured badge & priority search', included: false },
      { label: 'Analytics dashboard', included: false },
      { label: 'Lead capture form', included: false },
    ],
  },
  {
    name: 'Standard',
    price: PUBLIC_PLANS.standard.monthlyPrice,
    period: 'month',
    planKey: 'standard' as const,
    tagline: 'Full profile that converts browsers into customers.',
    cta: 'Get Standard',
    highlighted: true,
    badge: 'Most popular',
    features: [
      { label: 'Everything in Free', included: true },
      { label: 'Phone & email contact details', included: true },
      { label: 'Full business description', included: true },
      { label: 'Photo gallery (up to 10 photos)', included: true },
      { label: 'Up to 3 category placements', included: true },
      { label: 'Unlimited services listed', included: true },
      { label: 'Business hours display', included: true },
      { label: 'Featured listing badge', included: true },
      { label: 'Priority search placement', included: true },
      { label: 'Respond to customer reviews', included: true },
      { label: 'Social media links', included: true },
      { label: 'SEO backlink from MarineLink authority site', included: true },
      { label: 'Email support', included: true },
      { label: 'Premium badge & top placement', included: false },
      { label: 'Editable listing page', included: false },
      { label: 'Analytics dashboard', included: false },
    ],
  },
  {
    name: 'Premium',
    price: PUBLIC_PLANS.premium.monthlyPrice,
    period: 'month',
    planKey: 'premium' as const,
    tagline: 'Maximum visibility, leads & control.',
    cta: 'Get Premium',
    highlighted: false,
    badge: 'Best value',
    features: [
      { label: 'Everything in Standard', included: true },
      { label: 'Premium gold badge', included: true },
      { label: 'Top-of-search placement', included: true },
      { label: 'Fully editable listing page', included: true },
      { label: 'Unlimited category placements', included: true },
      { label: 'Unlimited photo gallery', included: true },
      { label: 'Advanced analytics (views, clicks, calls)', included: true },
      { label: 'Lead capture form with auto-forward', included: true },
      { label: 'Promoted across related categories', included: true },
      { label: 'Verified business badge', included: true },
      { label: 'Priority support (phone + email)', included: true },
      { label: 'Monthly performance report', included: true },
      { label: 'High-authority SEO backlink', included: true },
    ],
  },
];

const faqs = [
  {
    q: 'What\'s included in the free listing?',
    a: 'A free listing includes your business name and a link to your website - that\'s it. It\'s a directory entry with an SEO backlink from MarineLink. No phone number, no email, no photos, no description. It\'s enough to get your name on the list and get the SEO benefit, but any business that wants to actually convert visitors into customers should upgrade to Standard for the full profile.',
  },
  {
    q: 'Why is MarineLink good for my SEO?',
    a: 'MarineLink.co.nz is New Zealand\'s dedicated marine industry directory - an authority site for the NZ marine sector. A backlink from MarineLink signals relevance and trust to Google, boosting your own site\'s search rankings for marine-related keywords. Premium listings get a do-follow backlink from a high-authority domain, which is one of the strongest SEO signals you can get.',
  },
  {
    q: 'What does "editable listing page" mean?',
    a: 'Premium listings can be fully customised - edit your listing page layout, add custom content sections, embed videos, showcase your brand with a hero image, and control exactly how your business appears. Standard listings use a template layout that you fill in, but can\'t restructure.',
  },
  {
    q: 'Can I advertise on MarineLink?',
    a: 'Yes. Banner advertising is available for $49/month. Your banner appears on the MarineLink homepage and category pages, rotating with other advertisers. This is separate from listing pricing - you can advertise even if you don\'t have a listing. Contact us to set up advertising.',
  },
  {
    q: 'How does search ranking work?',
    a: 'Free listings appear in standard search order. Standard listings get priority placement above free results. Premium listings appear at the very top of relevant searches and across related category pages - maximum visibility.',
  },
  {
    q: 'Can I change plans or cancel anytime?',
    a: 'Yes. You can upgrade, downgrade, or cancel your subscription at any time from your account dashboard. Annual plans are billed yearly, and changes take effect at the next billing cycle.',
  },
];

export default function Pricing() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual');
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  const trackEvent = (name: string, properties: Record<string, string> = {}) => {
    fetch('/.netlify/functions/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, properties }),
      keepalive: true,
    }).catch(() => undefined);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="relative overflow-hidden py-16 md:py-24 px-4 md:px-8 text-center">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(79, 179, 217, 0.12), transparent 70%)' }}
        />
        <div className="relative z-10 max-w-[640px] mx-auto">
          <h1 className="font-display text-[1.75rem] sm:text-[2.5rem] md:text-[3rem] font-extrabold tracking-tight mb-4">
            Pricing that grows your marine business.
          </h1>
          <p className="text-text-secondary text-sm md:text-lg mb-8 leading-relaxed">
            Start free forever. Upgrade when you want the full profile - contact details, photos, services, and more. All plans include an SEO backlink from New Zealand's marine industry authority.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 p-1 bg-surface-light dark:bg-surface border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-md">
            <button
              onClick={() => { setBilling('monthly'); trackEvent('pricing_billing_changed', { billing_period: 'monthly' }); }}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                billing === 'monthly'
                  ? 'bg-accent text-white dark:text-bg'
                  : 'text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => { setBilling('annual'); trackEvent('pricing_billing_changed', { billing_period: 'annual' }); }}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                billing === 'annual'
                  ? 'bg-accent text-white dark:text-bg'
                  : 'text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary'
              }`}
            >
              Annual
              <span className="ml-1.5 text-[0.65rem] text-green dark:text-green font-bold">Best value</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section id="plans" className="max-w-[1200px] mx-auto px-4 md:px-8 pb-12 md:pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 items-start">
          {plans.map((plan) => {
            const planKey = plan.planKey as PlanKey;
            const planConfig = PUBLIC_PLANS[planKey];
            const isPaid = planConfig.monthlyPrice > 0;
            const displayPrice = isPaid ? planConfig.monthlyPrice : 0;
            const annualTotalAmount = isPaid ? annualTotal(planConfig.monthlyPrice, 12) : 0;
            const formattedPrice = String(displayPrice);
            const billingNote = !isPaid
              ? 'Free forever'
              : billing === 'annual'
                ? `Billed annually ($${annualTotalAmount}/yr)`
                : 'Billed monthly';
            const ctaLink = planKey === 'free'
              ? '/list?plan=free'
              : `/list?plan=${planKey}&billing=${billing}`;

            return (
              <div
                key={plan.name}
                className={`relative rounded-xl p-6 md:p-8 transition-all ${
                  plan.highlighted
                    ? 'border-2 border-accent bg-card-light dark:bg-card shadow-light-md dark:shadow-lg md:scale-[1.03] md:-mt-2'
                    : 'border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] bg-card-light dark:bg-card hover:border-[rgba(79,179,217,0.25)]'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`inline-block px-3 py-1 rounded-full text-[0.65rem] font-bold uppercase tracking-wider ${
                      plan.badge === 'Most popular'
                        ? 'bg-accent text-white dark:text-bg'
                        : 'bg-green text-white'
                    }`}>
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Plan name */}
                <h3 className="font-display text-lg md:text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-text-secondary text-sm mb-5">{plan.tagline}</p>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-display text-[2.25rem] md:text-[2.75rem] font-extrabold">
                    {plan.price === 0 ? '$0' : `$${formattedPrice}`}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-text-muted text-sm">/month</span>
                  )}
                </div>
                <p className="text-text-muted text-xs mb-6">{billingNote}</p>

                {/* CTA */}
                <Link
                  to={ctaLink}
                  onClick={() => trackEvent('pricing_plan_selected', { plan: planKey, billing_period: billing })}
                  className={`btn btn-block mb-7 ${plan.highlighted ? 'btn-primary' : 'btn-ghost'}`}
                >
                  {plan.cta}
                </Link>

                {/* Features */}
                <ul className="flex flex-col gap-3">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      {f.included ? (
                        <span className="flex-shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-green" />
                        </span>
                      ) : (
                        <span className="flex-shrink-0 mt-0.5">
                          <X className="w-4 h-4 text-text-muted opacity-50" />
                        </span>
                      )}
                      <span className={f.included ? 'text-text-primary-light dark:text-text-primary' : 'text-text-muted line-through'}>
                        {f.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* SEO / Authority value section */}
      <section className="bg-surface-light dark:bg-surface border-t border-b border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="font-display text-lg md:text-[1.5rem] font-bold text-center mb-2 md:mb-3">Why a MarineLink listing matters for SEO</h2>
          <p className="text-text-secondary text-sm text-center max-w-[640px] mx-auto mb-6 md:mb-8">
            MarineLink is New Zealand's authority site for the marine industry. A listing here does more than get you found - it boosts your Google ranking.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 md:gap-8">
            {[
              {
                icon: '🔗',
                title: 'Authority backlink',
                desc: 'A backlink from MarineLink.co.nz signals to Google that your business is relevant and trusted in the NZ marine industry. Premium listings get a do-follow link - one of the strongest SEO signals available.',
              },
              {
                icon: '🔍',
                title: 'Rank for marine keywords',
                desc: 'MarineLink ranks for hundreds of NZ marine search terms. Your listing captures that traffic, funnelling qualified leads to your business - even if your own website is new.',
              },
              {
                icon: '📍',
                title: 'Local SEO boost',
                desc: 'MarineLink listings appear in local search results and Google Maps. A verified listing with consistent NAP (name, address, phone) data strengthens your local search presence.',
              },
              {
                icon: '📈',
                title: 'More than a directory',
                desc: 'MarineLink is a content platform - marine news, category guides, and industry resources. That content authority flows through to every listed business via association and backlinks.',
              },
            ].map((item) => (
              <div key={item.title} className="text-center px-2">
                <div className="text-[1.75rem] mb-3">{item.icon}</div>
                <h3 className="font-semibold text-sm md:text-base mb-2">{item.title}</h3>
                <p className="text-text-secondary text-xs md:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advertising section */}
      <section id="advertising" className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="rounded-xl border-2 border-[rgba(79,179,217,0.2)] dark:border-[rgba(79,179,217,0.25)] bg-gradient-to-br from-card-light to-surface-light dark:from-card dark:to-surface p-6 md:p-10 overflow-hidden relative">
          <div
            className="absolute top-[-40%] right-[-10%] w-[500px] h-[500px] pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(79, 179, 217, 0.1), transparent 70%)' }}
          />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-block px-3 py-1 rounded-full bg-accent/10 text-accent text-[0.65rem] font-bold uppercase tracking-wider mb-4">
                Advertising
              </div>
              <h2 className="font-display text-xl md:text-[2rem] font-bold mb-3">
                Advertise on MarineLink
              </h2>
              <p className="text-text-secondary text-sm md:text-base mb-4 leading-relaxed">
                Get your brand in front of thousands of boaties, sailors, and marine industry professionals across New Zealand. Banner ads appear on the MarineLink homepage and category pages.
              </p>
              <ul className="flex flex-col gap-2 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green mt-0.5 flex-shrink-0" />
                  Banner ad on homepage + category pages
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green mt-0.5 flex-shrink-0" />
                  Rotated with other advertisers for fair exposure
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green mt-0.5 flex-shrink-0" />
                  Impression & click tracking
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green mt-0.5 flex-shrink-0" />
                  No long-term contract - cancel anytime
                </li>
              </ul>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-display text-[2.25rem] font-extrabold">${ADVERTISING_PLAN.monthlyPrice}</span>
                <span className="text-text-muted text-sm">/month</span>
              </div>
              <p className="text-text-muted text-xs mb-5">Billed monthly via Stripe. No setup fee.</p>
              <Link
                to="/contact?topic=advertising"
                onClick={() => trackEvent('advertising_cta_clicked', { plan: 'advertising' })}
                className="btn btn-primary text-sm md:text-base"
              >
                Enquire about advertising
              </Link>
            </div>
            {/* Mock banner preview */}
            <div className="relative">
              <div className="rounded-lg overflow-hidden border border-[rgba(79,179,217,0.15)] dark:border-[rgba(79,179,217,0.2)] bg-surface dark:bg-surface-dark">
                <div className="px-4 py-2 bg-[rgba(79,179,217,0.08)] border-b border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)]">
                  <span className="text-[0.6rem] text-text-muted uppercase tracking-wider">Sponsored - Your banner here</span>
                </div>
                <div className="p-6 md:p-8 flex items-center justify-center min-h-[120px] bg-gradient-to-r from-[rgba(79,179,217,0.05)] to-[rgba(79,179,217,0.02)]">
                  <div className="text-center">
                    <div className="font-display text-lg md:text-xl font-bold text-accent mb-1">Your Business Name</div>
                    <div className="text-text-secondary text-xs md:text-sm">Premium marine services across NZ</div>
                  </div>
                </div>
              </div>
              <p className="text-text-muted text-xs text-center mt-3">Example banner - design included with your subscription</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison strip */}
      <section className="bg-surface-light dark:bg-surface border-t border-b border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="font-display text-lg md:text-[1.5rem] font-bold text-center mb-6 md:mb-8">Why upgrade?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
            {[
              {
                icon: '📊',
                title: 'Get found faster',
                desc: 'Standard and Premium listings rank above free results in search. Premium listings appear at the very top - and across related category pages.',
              },
              {
                icon: '🎯',
                title: 'Convert more leads',
                desc: 'Premium listings include a lead capture form right on the listing page. Enquiries are forwarded to your inbox automatically - no follow-up, no missed leads.',
              },
              {
                icon: '🔗',
                title: 'Build SEO authority',
                desc: 'A backlink from MarineLink - NZ\'s marine industry authority - boosts your Google ranking. Premium listings get a do-follow link for maximum SEO impact.',
              },
            ].map((item) => (
              <div key={item.title} className="text-center px-2">
                <div className="text-[1.75rem] mb-3">{item.icon}</div>
                <h3 className="font-semibold text-sm md:text-base mb-2">{item.title}</h3>
                <p className="text-text-secondary text-xs md:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[800px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <h2 className="font-display text-lg md:text-[1.5rem] font-bold text-center mb-8 md:mb-10">Frequently asked questions</h2>
        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => (
            <FAQItem key={i} {...faq} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-4 md:px-8 pb-12 md:pb-16">
        <div className="bg-gradient-to-br from-surface-light to-elevated-light dark:from-surface dark:to-elevated border border-[rgba(79,179,217,0.2)] dark:border-[rgba(79,179,217,0.25)] rounded-xl p-6 md:p-12 text-center relative overflow-hidden">
          <div
            className="absolute top-[-50%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(79, 179, 217, 0.15), transparent 70%)' }}
          />
          <h2 className="font-display text-xl md:text-[2rem] font-bold mb-4 relative">Ready to grow your marine business?</h2>
          <p className="text-text-secondary max-w-[520px] mx-auto mb-6 relative text-sm md:text-base px-2">
            Join 150+ marine businesses already on MarineLink. Free forever - upgrade anytime.
          </p>
          <Link to="/list" className="btn btn-primary relative text-sm md:text-base">
            List your business - free forever
          </Link>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg overflow-hidden bg-card-light dark:bg-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-sm font-semibold">{q}</span>
        <span className={`flex-shrink-0 text-accent text-lg transition-transform ${open ? 'rotate-45' : ''}`}>
          +
        </span>
      </button>
      {open && (
        <div className="px-5 pb-4 text-text-secondary text-sm leading-relaxed">{a}</div>
      )}
    </div>
  );
}
